import React from 'react';
import { X } from 'lucide-react';
import type { SeriesFilters } from '../../hooks/useResourceSeries';

interface Props {
  filters: SeriesFilters;
  onChange: (next: Partial<SeriesFilters>) => void;
  availableTags: string[];
  availableBooks: string[];
}

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
        active
          ? 'bg-electric text-white border-electric shadow-sm'
          : 'bg-white dark:bg-midnight/40 text-slate dark:text-soft-white/70 border-sand/40 dark:border-slate/30 hover:border-electric/50 hover:text-electric'
      }`}
    >
      {active && <span className="text-[10px]">✓</span>}
      {label}
    </button>
  );
}

export function SeriesFilterSidebar({ filters, onChange, availableTags, availableBooks }: Props) {
  const toggleBook = (book: string) => {
    onChange({
      books: filters.books.includes(book)
        ? filters.books.filter(b => b !== book)
        : [...filters.books, book],
    });
  };

  const toggleTopic = (topic: string) => {
    onChange({
      topics: filters.topics.includes(topic)
        ? filters.topics.filter(t => t !== topic)
        : [...filters.topics, topic],
    });
  };

  const hasActiveFilters = filters.books.length > 0 || filters.topics.length > 0;

  return (
    <aside className="w-56 shrink-0 space-y-5 pb-8">

      {/* Active filter count + clear */}
      {hasActiveFilters && (
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-electric">
            {filters.books.length + filters.topics.length} filter{filters.books.length + filters.topics.length !== 1 ? 's' : ''} active
          </span>
          <button
            onClick={() => onChange({ books: [], topics: [] })}
            className="text-xs text-slate dark:text-soft-white/50 hover:text-red-400 flex items-center gap-1 transition-colors"
          >
            <X className="h-3 w-3" /> Clear
          </button>
        </div>
      )}

      {/* Tags — derived from actual published series */}
      {availableTags.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-slate dark:text-soft-white/40 uppercase tracking-widest mb-2.5">Topic</p>
          <div className="flex flex-wrap gap-1.5">
            {availableTags.map(tag => (
              <Chip
                key={tag}
                label={tag}
                active={filters.topics.includes(tag)}
                onClick={() => toggleTopic(tag)}
              />
            ))}
          </div>
        </div>
      )}

      {availableTags.length > 0 && availableBooks.length > 0 && (
        <div className="border-t border-sand/20 dark:border-slate/20" />
      )}

      {/* Book of Bible — only books with published series */}
      {availableBooks.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-slate dark:text-soft-white/40 uppercase tracking-widest mb-2.5">Book of Bible</p>
          <div className="flex flex-wrap gap-1.5">
            {availableBooks.map(book => (
              <Chip
                key={book}
                label={book}
                active={filters.books.includes(book)}
                onClick={() => toggleBook(book)}
              />
            ))}
          </div>
        </div>
      )}

      {(availableTags.length > 0 || availableBooks.length > 0) && (
        <div className="border-t border-sand/20 dark:border-slate/20" />
      )}

      {/* Series length */}
      <div>
        <p className="text-[10px] font-semibold text-slate dark:text-soft-white/40 uppercase tracking-widest mb-2.5">Series Length</p>
        <div className="flex items-center gap-2">
          <input
            type="number" min={1} max={52} value={filters.weekMin}
            onChange={e => onChange({ weekMin: Number(e.target.value) })}
            className="w-14 px-2 py-1.5 text-xs text-center border border-sand/40 dark:border-slate/30 rounded-lg bg-white dark:bg-midnight/50 text-midnight dark:text-soft-white focus:outline-none focus:ring-2 focus:ring-electric/30"
          />
          <span className="text-xs text-slate dark:text-soft-white/40">–</span>
          <input
            type="number" min={1} max={52} value={filters.weekMax}
            onChange={e => onChange({ weekMax: Number(e.target.value) })}
            className="w-14 px-2 py-1.5 text-xs text-center border border-sand/40 dark:border-slate/30 rounded-lg bg-white dark:bg-midnight/50 text-midnight dark:text-soft-white focus:outline-none focus:ring-2 focus:ring-electric/30"
          />
          <span className="text-xs text-slate dark:text-soft-white/40">wks</span>
        </div>
      </div>
    </aside>
  );
}
